"use client";

import type { CatalogPiece } from "@/lib/api/api-client";
import { ActionsMenu } from "./actions-menu";
import { PieceCard } from "./piece-card";
import { PieceMenu } from "./piece-menu";

/**
 * La carte d'un morceau **du catalogue** : la tuile, le signet quand il est
 * déjà rangé, et le menu qui permet de le ranger.
 *
 * # Pourquoi un composant client dédié
 *
 * Les surfaces du catalogue sont rendues par le serveur : l'accueil, une
 * sélection, l'écran vide de la recherche. Le menu, lui, est une fonction de
 * rendu passée à `ActionsMenu` — une fermeture, donc impossible à faire
 * traverser la frontière serveur → client. Cette carte est le point de
 * bascule : les pages restent serveur, la carte est cliente.
 *
 * # Le signet se montre partout
 *
 * `CatalogPiece.inLibrary` arrive avec la fiche, sur `/api/pieces`, sur les
 * sélections et sur la fiche détaillée, en une requête par page et jamais une
 * par carte. Le signet peut donc paraître dès la grille, au lieu d'attendre
 * qu'on ouvre le morceau pour apprendre qu'on l'avait déjà.
 *
 * Il vaut toujours faux pour un visiteur anonyme : sans compte il n'y a pas de
 * bibliothèque, donc rien à signaler, et la carte n'affiche alors aucun état
 * plutôt qu'un « pas ajouté » qui promettrait un geste demandant d'abord un
 * compte.
 */
export function CatalogCard({
  piece,
  onOpen,
}: {
  piece: CatalogPiece;
  /** Remonté avant la navigation, pour la mesure des sélections. */
  onOpen?: () => void;
}) {
  return (
    <PieceCard
      piece={piece}
      onOpen={onOpen}
      bookmark
      /*
        Sur une surface de catalogue, la question est « est-ce en ligne » et non
        « qu'ai-je le droit d'en faire » : le catalogue public est en domaine
        public de bout en bout, et une pièce qui n'y est pas n'arrive ici qu'en
        prévisualisation admin. C'est là, et là seulement, que l'état de
        publication est l'information utile.
      */
      marking="publication"
      action={
        <ActionsMenu label={`Actions sur « ${piece.title} »`}>
          {(close) => <PieceMenu piece={piece} entry={null} owned={piece.inLibrary} close={close} />}
        </ActionsMenu>
      }
    />
  );
}
